import React from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { convertFirebaseTimestamp } from '../../services/funnelService';

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px);
  overflow-y: auto;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${theme.spacing.xl};
  min-height: 0;
`;

const Title = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.md};
`;

const InfoCard = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  width: 100%;
  max-width: 500px;
  box-shadow: ${theme.shadows.sm};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  text-align: left;
`;

const Label = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.textLight};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Value = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
`;

const EmptyState = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  font-style: italic;
`;

const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  
  const date = convertFirebaseTimestamp(timestamp) || timestamp;
  const dateObj = new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    return 'N/A';
  }
  
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const UserInfoScreen = ({ data, onNext, userProgress = {} }) => {
  const shortcode = userProgress?.shortcode || 'N/A';
  const journalEntries = userProgress?.journalEntries || [];
  const entryCount = journalEntries.length;
  
  // Get first and last journal entry dates
  let firstEntryDate = null;
  let lastEntryDate = null;
  
  if (journalEntries.length > 0) {
    // Sort entries by timestamp to ensure we get the correct first and last
    const sortedEntries = [...journalEntries].sort((a, b) => {
      const aTime = convertFirebaseTimestamp(a.timestamp) || a.timestamp;
      const bTime = convertFirebaseTimestamp(b.timestamp) || b.timestamp;
      return new Date(aTime) - new Date(bTime);
    });
    
    firstEntryDate = sortedEntries[0]?.timestamp;
    lastEntryDate = sortedEntries[sortedEntries.length - 1]?.timestamp;
  }
  
  return (
    <Container>
      <Content>
        {data?.content?.text && (
          <Title>{data.content.text}</Title>
        )}
        
        <InfoCard>
          <InfoRow>
            <Label>Shortcode</Label>
            <Value>{shortcode}</Value>
          </InfoRow>
          
          <InfoRow>
            <Label>Journal Entries</Label>
            <Value>{entryCount}</Value>
          </InfoRow>
          
          <InfoRow>
            <Label>First Entry Date</Label>
            <Value>
              {firstEntryDate ? formatDate(firstEntryDate) : <EmptyState>No entries yet</EmptyState>}
            </Value>
          </InfoRow>
          
          <InfoRow>
            <Label>Most Recent Entry Date</Label>
            <Value>
              {lastEntryDate ? formatDate(lastEntryDate) : <EmptyState>No entries yet</EmptyState>}
            </Value>
          </InfoRow>
        </InfoCard>
      </Content>
    </Container>
  );
};

export default UserInfoScreen;
